import {AxiosResponse} from 'axios';

import {TaskStatus} from 'enums';
import {Task} from 'types';

type Config<T, M = any> = {
    formatResponse: (data: T) => Task<T>;
    mergeResults: (acc: any, current: T) => M;
    onRequest: (data?: T) => Promise<T>;
    onProgress?: (progress: number) => void;
    onSuccess: (data: T | M) => void;
    onError?: (error: {response: AxiosResponse}) => void;
    onComplete?: () => void;
    requestPeriod?: number;
}

export class DataRequest<T, M = any> {
    private requestDataTimeoutId;
    private config: Config<T>;
    private defaultRequestPeriod = 500;
    private isLoading: boolean;
    private fullResponse: M = null;

    constructor(config: Config<T>) {
        this.config = config;
    }

    get inProgress() {
        return this.isLoading;
    }

    execute = async () => {
        this.fullResponse = null;
        try {
            this.isLoading = true;
            let data: any = await this.getData();
            let {status, progress} = data.task;
            let response = data.response;

            while (status !== TaskStatus.Completed && status !== TaskStatus.Failed) {
                data = await this.getDataWithTimeout(response);
                status = data.task.status;
                progress = data.task.progress;
                response = data.response;
                this.config.onProgress?.(progress);
            }

            if (status === TaskStatus.Completed) {
                this.config.onSuccess(this.fullResponse);
            } else {
                throw new Error('Task failed');
            }
        } catch (error: any) {
            this.cancel();
            this.config.onError?.(error);
        } finally {
            this.isLoading = false;
            this.config.onComplete?.();
        }
    };

    cancel = () => {
        clearTimeout(this.requestDataTimeoutId);
        this.requestDataTimeoutId = null;
    };

    private getData = async (response?: T): Promise<{task: Task<T>, response: T}> => {
        const currentResponse = await this.config.onRequest(response);
        this.mergeResults(currentResponse);
        return {response: currentResponse, task: this.config.formatResponse(currentResponse)};
    };

    private mergeResults(response: T) {
        this.fullResponse = this.config.mergeResults(this.fullResponse, response);
    }

    private getDataWithTimeout = (response: T): Promise<{task: Task<T>}> =>
        new Promise((resolve, reject) => {
            const timeoutId = setTimeout(async () => {
                try {
                    resolve(await this.getData(response));
                } catch (error) {
                    reject(error);
                }
            }, this.config.requestPeriod || this.defaultRequestPeriod);
            this.requestDataTimeoutId = timeoutId;
        });
}